/**
 * Budget utility functions for time-aware budget tracking
 */

export type BudgetType = 'monthly' | 'quarterly' | 'yearly';

/**
 * Calculate what percentage of the budget period has elapsed
 */
export function getTimeElapsedPercentage(type: BudgetType, date: Date = new Date()): number {
  // Normalize to start of day in local timezone to avoid timezone issues
  const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (type === 'monthly') {
    const dayOfMonth = normalizedDate.getDate();
    const daysInMonth = new Date(normalizedDate.getFullYear(), normalizedDate.getMonth() + 1, 0).getDate();
    return (dayOfMonth / daysInMonth) * 100;
  } else if (type === 'quarterly') {
    const month = normalizedDate.getMonth();
    const quarterStartMonth = Math.floor(month / 3) * 3;
    const quarterStart = new Date(normalizedDate.getFullYear(), quarterStartMonth, 1);
    const quarterEnd = new Date(normalizedDate.getFullYear(), quarterStartMonth + 3, 1);

    // Calculate days more reliably
    const msPerDay = 1000 * 60 * 60 * 24;
    const daysSinceQuarterStart = Math.floor((normalizedDate.getTime() - quarterStart.getTime()) / msPerDay) + 1;
    const daysInQuarter = Math.floor((quarterEnd.getTime() - quarterStart.getTime()) / msPerDay);

    return (daysSinceQuarterStart / daysInQuarter) * 100;
  } else { // yearly
    const startOfYear = new Date(normalizedDate.getFullYear(), 0, 1);
    const endOfYear = new Date(normalizedDate.getFullYear() + 1, 0, 1);

    const msPerDay = 1000 * 60 * 60 * 24;
    const daysSinceYearStart = Math.floor((normalizedDate.getTime() - startOfYear.getTime()) / msPerDay) + 1;
    const daysInYear = Math.floor((endOfYear.getTime() - startOfYear.getTime()) / msPerDay);

    return (daysSinceYearStart / daysInYear) * 100;
  }
}

/**
 * Calculate the spending pace relative to time elapsed
 * Positive value means spending faster than time elapsed
 * Negative value means spending slower than time elapsed
 */
export function getSpendingPace(percentSpent: number, type: BudgetType, date: Date = new Date()): number {
  const timeElapsed = getTimeElapsedPercentage(type, date);
  return percentSpent - timeElapsed;
}

/**
 * Check if the budget period is in the past (completed)
 */
function isPastPeriod(type: BudgetType, date: Date = new Date()): boolean {
  const now = new Date();
  const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (type === 'monthly') {
    // Check if we're in a future month
    const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const targetMonth = new Date(normalizedDate.getFullYear(), normalizedDate.getMonth(), 1);
    return targetMonth < currentMonth;
  } else if (type === 'quarterly') {
    const currentQuarterStart = Math.floor(now.getMonth() / 3) * 3;
    const targetQuarterStart = Math.floor(normalizedDate.getMonth() / 3) * 3;
    const currentQuarterDate = new Date(now.getFullYear(), currentQuarterStart, 1);
    const targetQuarterDate = new Date(normalizedDate.getFullYear(), targetQuarterStart, 1);
    return targetQuarterDate < currentQuarterDate;
  } else { // yearly
    return normalizedDate.getFullYear() < now.getFullYear();
  }
}

/**
 * Get badge variant based on time-aware spending analysis
 */
export function getBudgetBadgeVariant(percentSpent: number, type: BudgetType, date: Date = new Date(), isFixedExpense = false): 'danger' | 'warning' | 'success' {
  // Fixed expenses: expect exact amount
  if (isFixedExpense) {
    if (percentSpent > 105) return 'danger';  // More than 5% over expected
    if (percentSpent < 80) return 'warning';  // Less than 80% (might have missed payment)
    return 'success';  // Within 80-105% is fine
  }

  // Regular spending budgets
  if (percentSpent > 100) return 'danger';

  // For past periods, use simple percentage-based logic
  if (isPastPeriod(type, date)) {
    if (percentSpent >= 95) return 'warning';
    return 'success';
  }

  // For current period, use time-aware logic
  const spendingPace = getSpendingPace(percentSpent, type, date);

  // If close to budget (>90%) but still have time left, show warning
  if (percentSpent >= 90) return 'warning';

  // If spending significantly faster than time elapsed (>15% ahead), show warning
  if (spendingPace > 15) return 'warning';

  // If spending moderately fast (>8% ahead), show warning
  if (spendingPace > 8) return 'warning';

  return 'success';
}

/**
 * Get status text based on time-aware spending analysis
 */
export function getBudgetStatusText(percentSpent: number, type: BudgetType, date: Date = new Date(), isFixedExpense = false): string {
  // Fixed expenses: expect exact amount
  if (isFixedExpense) {
    if (percentSpent > 105) return 'Over Expected';
    if (percentSpent < 80) return 'Under Expected';
    if (percentSpent >= 95 && percentSpent <= 105) return 'On Track';  // Within 5% is perfect
    return 'On Track';  // Within 80-95% is still fine
  }

  // Regular spending budgets
  if (percentSpent > 100) return 'Over Budget';

  // For past periods, use simple percentage-based logic
  if (isPastPeriod(type, date)) {
    if (percentSpent >= 95) return 'Near Limit';
    return 'On Track';
  }

  // For current period, use time-aware logic
  const spendingPace = getSpendingPace(percentSpent, type, date);

  // Check if at/near 90% limit first (regardless of pace)
  if (percentSpent >= 90) {
    return 'Near Limit';
  }

  // Spending significantly faster than time elapsed
  if (spendingPace > 15) return 'Spending Fast';
  if (spendingPace > 8) return 'Above Pace';

  // Spending on track (within reasonable margin)
  if (spendingPace >= -5 && spendingPace <= 5) return 'On Track';

  // Spending slower than time elapsed
  if (spendingPace < -15) return 'Trending Under';

  return 'On Track';
}